import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/shared/components/ui/badge'
import { Skeleton } from '@/shared/components/ui/skeleton'
import { Separator } from '@/shared/components/ui/separator'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/shared/components/ui/table'
import { FileText, Building2, User, ShoppingBag } from 'lucide-react'
import { useDashboardComprobante } from '../hooks/useDashboard'

interface ComprobanteDetalleProps {
  grupoId: string
  comprobanteId: number
  open: boolean
  onOpenChange: (open: boolean) => void
  medicamentoResaltar?: string
}

export function ComprobanteDetalle({ grupoId, comprobanteId, open, onOpenChange, medicamentoResaltar }: ComprobanteDetalleProps) {
  const { data, isLoading } = useDashboardComprobante(grupoId, comprobanteId)

  const jsonData = data?.json_data as {
    establecimiento?: string
    sucursal?: string
    ruc_proveedor?: string
    numero_de_factura?: string
    numero_de_autorizacion?: string | null
    fecha_recibo?: string
    hora_recibo?: string
    nombre_cliente?: string
    ruc_cliente?: string
    email_cliente?: string
    precio_total?: number
    subtotal?: number
    iva?: number
    productos?: { codigo?: string; nombre?: string; cantidad?: number; precio_unitario?: number; precio_total?: number }[]
  } | undefined

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-teal-500" />
            Detalle de Factura
          </DialogTitle>
        </DialogHeader>

        {isLoading ? (
          <div className="space-y-4">
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-32 w-full" />
            <Skeleton className="h-32 w-full" />
          </div>
        ) : !data ? (
          <p className="text-muted-foreground">No se encontraron datos del comprobante</p>
        ) : (
          <div className="space-y-4">
            {/* Datos generales de la factura */}
            <Card className="border border-gray-300 dark:border-gray-700 bg-gradient-to-r from-teal-500/15 via-teal-500/5 via-50% to-transparent dark:from-teal-800/20 dark:via-teal-900/10 dark:via-50% dark:to-transparent">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <FileText className="h-4 w-4 text-teal-500" />
                  Comprobante
                  {jsonData?.numero_de_factura && (
                    <Badge variant="outline" className="text-xs border-teal-500">
                      #{String(jsonData.numero_de_factura)}
                    </Badge>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  {jsonData?.numero_de_factura && (
                    <div>
                      <span className="text-muted-foreground">N° Factura:</span>{' '}
                      <span className="font-medium">{String(jsonData.numero_de_factura)}</span>
                    </div>
                  )}
                  {jsonData?.fecha_recibo && (
                    <div>
                      <span className="text-muted-foreground">Fecha:</span>{' '}
                      <span className="font-medium">{String(jsonData.fecha_recibo)}</span>
                    </div>
                  )}
                  {jsonData?.hora_recibo && (
                    <div>
                      <span className="text-muted-foreground">Hora:</span>{' '}
                      <span className="font-medium">{String(jsonData.hora_recibo)}</span>
                    </div>
                  )}
                  {jsonData?.numero_de_autorizacion && (
                    <div className="min-w-0">
                      <span className="text-muted-foreground">N° Autorización:</span>{' '}
                      <span className="font-medium break-all">{String(jsonData.numero_de_autorizacion)}</span>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Establecimiento */}
            {jsonData?.establecimiento && (
              <Card className="border border-gray-300 dark:border-gray-700">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Building2 className="h-4 w-4 text-teal-500" />
                    Establecimiento
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div>
                      <span className="text-muted-foreground">Nombre:</span>{' '}
                      <span className="font-medium">{String(jsonData.establecimiento)}</span>
                    </div>
                    {jsonData.sucursal && (
                      <div>
                        <span className="text-muted-foreground">Sucursal:</span>{' '}
                        <span className="font-medium">{String(jsonData.sucursal)}</span>
                      </div>
                    )}
                    {jsonData.ruc_proveedor && (
                      <div>
                        <span className="text-muted-foreground">RUC:</span>{' '}
                        <span className="font-medium">{String(jsonData.ruc_proveedor)}</span>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Cliente */}
            {jsonData && (
              <Card className="border border-gray-300 dark:border-gray-700">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <User className="h-4 w-4 text-teal-500" />
                    Cliente
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div>
                      <span className="text-muted-foreground">Nombre:</span>{' '}
                      <span className="font-medium text-muted-foreground italic">No se registra</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Identificación:</span>{' '}
                      <span className="font-medium text-muted-foreground italic">No se registra</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Email:</span>{' '}
                      <span className="font-medium text-muted-foreground italic">No se registra</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            <Separator />

            {/* Totales */}
            {(jsonData?.subtotal != null || jsonData?.precio_total != null) && (
              <Card className="border border-gray-300 dark:border-gray-700 bg-gradient-to-r from-teal-500/15 via-teal-500/5 via-50% to-transparent dark:from-teal-800/20 dark:via-teal-900/10 dark:via-50% dark:to-transparent">
                <CardContent className="pt-4">
                  <div className="grid grid-cols-3 gap-4 text-center">
                    {jsonData.subtotal != null && (
                      <div>
                        <div className="text-2xl font-bold">${Number(jsonData.subtotal).toFixed(2)}</div>
                        <div className="text-xs text-muted-foreground">Subtotal</div>
                      </div>
                    )}
                    {jsonData.iva != null && (
                      <div>
                        <div className="text-2xl font-bold">${Number(jsonData.iva).toFixed(2)}</div>
                        <div className="text-xs text-muted-foreground">IVA</div>
                      </div>
                    )}
                    {jsonData.precio_total != null && (
                      <div>
                        <div className="text-2xl font-bold">${Number(jsonData.precio_total).toFixed(2)}</div>
                        <div className="text-xs text-muted-foreground">Total</div>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Productos */}
            {jsonData?.productos && jsonData.productos.length > 0 && (
              <Card className="border border-gray-300 dark:border-gray-700">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <ShoppingBag className="h-4 w-4 text-teal-500" />
                    Productos
                    <Badge variant="secondary" className="text-xs">
                      {jsonData.productos.length}
                    </Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="rounded-md border border-gray-300 dark:border-gray-700 overflow-x-auto">
                    <Table>
                      <TableHeader className="bg-gray-100 dark:bg-[hsl(220,15%,16%)]">
                        <TableRow>
                          <TableHead className="whitespace-nowrap text-xs">Código</TableHead>
                          <TableHead className="whitespace-nowrap text-xs">Nombre</TableHead>
                          <TableHead className="whitespace-nowrap text-xs text-right">Cant.</TableHead>
                          <TableHead className="whitespace-nowrap text-xs text-right">P. Unit.</TableHead>
                          <TableHead className="whitespace-nowrap text-xs text-right">P. Total</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {jsonData.productos.map((prod, idx) => {
                          const esResaltado = medicamentoResaltar && prod.nombre &&
                            String(prod.nombre).toLowerCase().includes(medicamentoResaltar.toLowerCase())
                          return (
                          <TableRow key={idx} className={esResaltado ? 'bg-yellow-100 dark:bg-yellow-900/30' : ''}>
                            <TableCell className="text-xs whitespace-nowrap">
                              {prod.codigo != null ? String(prod.codigo) : '-'}
                            </TableCell>
                            <TableCell className="text-xs">
                              {esResaltado ? (
                                <span className="bg-yellow-200 dark:bg-yellow-700/50 px-1 rounded font-semibold">
                                  {String(prod.nombre)}
                                </span>
                              ) : (
                                prod.nombre != null ? String(prod.nombre) : '-'
                              )}
                            </TableCell>
                            <TableCell className="text-xs text-right whitespace-nowrap">
                              {prod.cantidad != null ? String(prod.cantidad) : '-'}
                            </TableCell>
                            <TableCell className="text-xs text-right whitespace-nowrap">
                              {prod.precio_unitario != null ? `$${Number(prod.precio_unitario).toFixed(2)}` : '-'}
                            </TableCell>
                            <TableCell className="text-xs text-right whitespace-nowrap">
                              {prod.precio_total != null ? `$${Number(prod.precio_total).toFixed(2)}` : '-'}
                            </TableCell>
                          </TableRow>
                          )
                        })}
                      </TableBody>
                    </Table>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
